import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { ButtonLink, DigitalKey } from '@/ds'
import { telHref } from '@/lib/format'
import { whatsappHref } from '@/lib/whatsapp'
import { useAppNavigate } from '@/lib/query-params'
import type { KeyStatus } from '@/services/accesos'
import type { Deposit } from '@/services/fianza'
import type { PaymentSummary } from '@/services/pagos'
import type { Stay } from '@/services/reserva'
import { changeQuery } from '@/app/screens/Reserva/shared'
import type { ChangeOrigin } from '@/app/screens/Reserva/shared'
import { lockedKeyContent, MISSING_ROUTES, reportKeyDiscrepancy } from './missing'

interface Props {
  stay: Stay
  keyStatus: KeyStatus
  payment: PaymentSummary | null
  deposit: Deposit | null
  now: Date
  /** Pantalla a la que vuelven el pago y la fianza (B4) */
  origin: ChangeOrigin
}

/**
 * Llave bloqueada con sus motivos (inventario D6), la misma en Tu estancia y en Accesos: cada motivo con enlace a
 * su paso; sin motivos antes de la hora, cuándo se activa; sin motivos y ya pasada la hora, el motivo genérico con
 * el contacto de la propiedad. Nunca un motivo inventado.
 */
export function LockedKey({ stay, keyStatus, payment, deposit, now, origin }: Props) {
  const { t, i18n } = useTranslation()
  const { go } = useAppNavigate()
  const goReason = (path: string) => (path === MISSING_ROUTES.register ? go(path) : go(path, { query: changeQuery(origin) }))
  const { lockedText, items, generic } = lockedKeyContent(stay, keyStatus, payment, deposit, now, i18n.language, t, goReason)
  const phone = stay.contact.supportPhone
  return (
    <div className="accesos__section">
      <DigitalKey state="locked" lockedText={lockedText} missing={items} />
      {generic && stay.contact.whatsapp && (
        <ButtonLink variant="secondary" icon="message-circle" href={whatsappHref(stay.contact.whatsapp, stay.reference)} target="_blank" rel="noreferrer">
          {t('v2.ds.contact.whatsapp')}
        </ButtonLink>
      )}
      {generic && !stay.contact.whatsapp && phone && (
        <ButtonLink variant="secondary" icon="phone" href={telHref(phone)}>
          {t('v2.ds.contact.llamar')}
        </ButtonLink>
      )}
    </div>
  )
}

/** Registra en Sentry (D6) una llave activa por `showDomotic` con motivos que el cliente sí ve pendientes. */
export function useKeyDiscrepancy(stay: Stay, keyStatus: KeyStatus | null, payment: PaymentSummary | null, deposit: Deposit | null, ready: boolean, now: Date) {
  useEffect(() => {
    if (ready && keyStatus) reportKeyDiscrepancy(stay, keyStatus, payment, deposit, now)
    // Solo al tener los datos; `now` cambia cada minuto y no debe repetir el evento (además se deduplica).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stay, keyStatus, payment, deposit, ready])
}
